import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'ai_chat_client';

const SESSIONS_BASE = `${BACKEND_URL}ai-chat/sessions/`;

/**
 * Flatten DRF validation / error payloads from ai-chat endpoints.
 *
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export const formatAiChatErrors = errPayload => {
  if (errPayload == null) {
    return ['Request failed. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(item => String(item));
  }
  if (errPayload.error && typeof errPayload.error === 'string') {
    return [errPayload.error];
  }
  if (errPayload.detail && typeof errPayload.detail === 'string') {
    return [errPayload.detail];
  }
  if (errPayload.content) {
    const content = errPayload.content;
    if (Array.isArray(content)) {
      return content.map(v => `content: ${v}`);
    }
    return [`content: ${String(content)}`];
  }
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(v => messages.push(`${key}: ${v}`));
    } else if (typeof value === 'object') {
      messages.push(`${key}: ${JSON.stringify(value)}`);
    } else {
      messages.push(String(value));
    }
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

/**
 * Map HTTP status to user-facing copy for Materiah AI chat failures.
 *
 * @param {number|undefined|null} status
 * @param {string[]} [messages]
 * @returns {string}
 */
export const getAiChatUserMessage = (status, messages = []) => {
  if (status === 503) {
    return 'Assistant unavailable';
  }
  if (status === 502) {
    return 'The AI service failed. Please try again.';
  }
  if (status === 403) {
    return 'You do not have access to the assistant.';
  }
  if (status === 404) {
    return 'Chat session not found.';
  }
  if (status === 429) {
    return 'Too many requests. Please wait and try again.';
  }
  if (status === 400 && messages.length) {
    return messages[0];
  }
  if (status === 401) {
    return 'Please sign in again to use the assistant.';
  }
  return messages[0] || 'Request failed. Please try again.';
};

/**
 * Normalize a typed citation from an assistant message.
 * Defaults entity_type to product when only product_id is present (legacy shape).
 *
 * @param {Object|null} citation
 * @returns {Object|null}
 */
export const normalizeCitation = citation => {
  if (!citation || typeof citation !== 'object') return null;
  const entityType =
    citation.entity_type ||
    (citation.product_id != null
      ? 'product'
      : citation.order_id != null
        ? 'order'
        : citation.quote_id != null
          ? 'quote'
          : citation.supplier_id != null
            ? 'supplier'
            : citation.project_id != null
              ? 'project'
              : null);
  return {
    entity_type: entityType,
    product_id: citation.product_id ?? null,
    order_id: citation.order_id ?? null,
    quote_id: citation.quote_id ?? null,
    supplier_id: citation.supplier_id ?? null,
    project_id: citation.project_id ?? null,
    cat_num: citation.cat_num ?? '',
    name: citation.name ?? '',
    corporate_order_ref: citation.corporate_order_ref ?? '',
    source: citation.source ?? 'materiah',
  };
};

/**
 * Whether a normalized session should appear in the default (active) sidebar list.
 *
 * @param {Object|null} row
 * @returns {boolean}
 */
export const isActiveAiChatSession = row => {
  if (!row) return false;
  const status = String(row.status || 'active').toLowerCase();
  return status === 'active';
};

/**
 * Normalize a session list row from GET /ai-chat/sessions/.
 *
 * @param {Object|null} row
 * @returns {Object|null}
 */
export const normalizeSessionListRow = row => {
  if (!row) return null;
  return {
    id: row.id,
    title: row.title ?? '',
    status: row.status ?? 'active',
    created_at: row.created_at,
    updated_at: row.updated_at,
    message_count: row.message_count ?? 0,
  };
};

/**
 * Normalize a chat message (user or assistant) from session detail / send response.
 *
 * @param {Object|null} msg
 * @returns {Object|null}
 */
export const normalizeChatMessage = msg => {
  if (!msg) return null;
  return {
    id: msg.id,
    role: msg.role,
    content: msg.content ?? '',
    created_at: msg.created_at,
    citations: Array.isArray(msg.citations)
      ? msg.citations.map(normalizeCitation).filter(Boolean)
      : [],
    tool_trace: Array.isArray(msg.tool_trace) ? msg.tool_trace : [],
    prompt_tokens: msg.prompt_tokens ?? null,
    completion_tokens: msg.completion_tokens ?? null,
    openai_model: msg.openai_model ?? null,
  };
};

/**
 * @param {Object|null} session
 * @returns {Object|null}
 */
export const normalizeSessionDetail = session => {
  if (!session) return null;
  return {
    id: session.id,
    title: session.title ?? '',
    status: session.status ?? 'active',
    created_at: session.created_at,
    updated_at: session.updated_at,
    messages: Array.isArray(session.messages)
      ? session.messages.map(normalizeChatMessage).filter(Boolean)
      : [],
  };
};

/**
 * Build a failed client result from an axios error.
 *
 * @param {import('axios').AxiosError|Error} error
 * @param {string} op
 * @returns {{success:false,status?:number,error:*,messages:string[],userMessage:string}}
 */
function failFromError(error, op) {
  const status = error.response?.status;
  const errPayload = error.response?.data || error.message;
  const messages = formatAiChatErrors(errPayload);
  logError(LOG_CONTEXT, `${op} failed`, {
    status,
    message: error.message,
  });
  return {
    success: false,
    status,
    error: errPayload,
    messages,
    userMessage: getAiChatUserMessage(status, messages),
  };
}

/**
 * POST /v1/ai-chat/sessions/
 *
 * @param {string} token
 * @param {{ title?: string }} [options]
 * @returns {Promise<{success:boolean,data?:Object,status?:number,messages?:string[],userMessage?:string,error?:*}>}
 */
export const createAiChatSession = async (token, options = {}) => {
  const body = {};
  if (options.title != null && String(options.title).trim() !== '') {
    body.title = String(options.title).trim();
  }
  try {
    logInfo(LOG_CONTEXT, 'createAiChatSession start', {
      hasTitle: Boolean(body.title),
    });
    const response = await axios.post(SESSIONS_BASE, body, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = normalizeSessionDetail(response.data);
    logInfo(LOG_CONTEXT, 'createAiChatSession success', {
      sessionId: data?.id,
    });
    return { success: true, data };
  } catch (error) {
    return failFromError(error, 'createAiChatSession');
  }
};

/**
 * GET /v1/ai-chat/sessions/
 * Returns active sessions by default (archived soft-deletes are excluded client-side).
 *
 * @param {string} token
 * @param {Object} [options]
 * @param {'active'|'archived'|'all'} [options.status='active'] - Which sessions to include.
 * @returns {Promise<{success:boolean,results?:Object[],status?:number,messages?:string[],userMessage?:string,error?:*}>}
 */
export const listAiChatSessions = async (token, options = {}) => {
  const statusFilter = options.status || 'active';
  try {
    logInfo(LOG_CONTEXT, 'listAiChatSessions start', { statusFilter });
    const response = await axios.get(SESSIONS_BASE, {
      headers: { Authorization: `Token ${token}` },
    });
    const raw = Array.isArray(response.data)
      ? response.data
      : response.data?.results || [];
    let results = raw.map(normalizeSessionListRow).filter(Boolean);
    // Soft-archive keeps rows in list responses; hide them from the default sidebar.
    if (statusFilter === 'active') {
      results = results.filter(isActiveAiChatSession);
    } else if (statusFilter === 'archived') {
      results = results.filter(
        row => String(row.status || '').toLowerCase() === 'archived'
      );
    }
    logInfo(LOG_CONTEXT, 'listAiChatSessions success', {
      count: results.length,
      statusFilter,
    });
    return { success: true, results };
  } catch (error) {
    return failFromError(error, 'listAiChatSessions');
  }
};

/**
 * GET /v1/ai-chat/sessions/{id}/
 *
 * @param {string} token
 * @param {number|string} sessionId
 * @returns {Promise<{success:boolean,data?:Object,status?:number,messages?:string[],userMessage?:string,error?:*}>}
 */
export const getAiChatSession = async (token, sessionId) => {
  try {
    logInfo(LOG_CONTEXT, 'getAiChatSession start', { sessionId });
    const response = await axios.get(`${SESSIONS_BASE}${sessionId}/`, {
      headers: { Authorization: `Token ${token}` },
    });
    const data = normalizeSessionDetail(response.data);
    logInfo(LOG_CONTEXT, 'getAiChatSession success', {
      sessionId: data?.id,
      messageCount: data?.messages?.length,
    });
    return { success: true, data };
  } catch (error) {
    return failFromError(error, 'getAiChatSession');
  }
};

/**
 * DELETE /v1/ai-chat/sessions/{id}/ (soft-archive).
 *
 * @param {string} token
 * @param {number|string} sessionId
 * @returns {Promise<{success:boolean,status?:number,messages?:string[],userMessage?:string,error?:*}>}
 */
export const deleteAiChatSession = async (token, sessionId) => {
  try {
    logInfo(LOG_CONTEXT, 'deleteAiChatSession start', { sessionId });
    await axios.delete(`${SESSIONS_BASE}${sessionId}/`, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'deleteAiChatSession success', { sessionId });
    return { success: true };
  } catch (error) {
    return failFromError(error, 'deleteAiChatSession');
  }
};

/**
 * POST /v1/ai-chat/sessions/{id}/messages/
 *
 * @param {string} token
 * @param {number|string} sessionId
 * @param {string} content
 * @returns {Promise<{success:boolean,data?:Object,status?:number,messages?:string[],userMessage?:string,error?:*}>}
 */
export const sendAiChatMessage = async (token, sessionId, content) => {
  try {
    logInfo(LOG_CONTEXT, 'sendAiChatMessage start', {
      sessionId,
      contentLength: content?.length,
    });
    const response = await axios.post(
      `${SESSIONS_BASE}${sessionId}/messages/`,
      { content },
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const payload = response.data || {};
    const message = normalizeChatMessage(payload.message);
    logInfo(LOG_CONTEXT, 'sendAiChatMessage success', {
      sessionId: payload.session_id ?? sessionId,
      messageId: message?.id,
      citationCount: message?.citations?.length,
    });
    return {
      success: true,
      data: {
        session_id: payload.session_id ?? sessionId,
        message,
        usage: payload.usage || null,
      },
    };
  } catch (error) {
    return failFromError(error, 'sendAiChatMessage');
  }
};
